// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSubscriptionStore from '@/app/store/modules/subscription';
import actionsPosTerminals from '../../../api/actionsPosTerminals';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import SubscriptionsTable from '@/app/modules/subscriptions/components/tables/SubscriptionsTable';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import FooterClose from '@/common-utils/components/renders/modal/components/footers/FooterClose/FooterClose';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Modal, ModalContent, ModalBody } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import subscriptionFields from '@/app/modules/subscriptions/fields/subscriptionFields';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
// Lang
import { nsTerminal } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @returns {JSX} ModalMutipleRewarehouse
 */
function ModalMutipleRewarehouse({ isOpen, setIsOpen }) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const storeRefetchSubscriptions = useSubscriptionStore(s => s.storeRefetchSubscriptions);

    // Toast
    const { toast } = useToast();

    // Constants
    const MAX_STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.multiple_rewarehouse.steps';
    const customQuery = {
        filters: [
            {
                key: subscriptionFields.PROVIDER.key,
                value: rootOrganization.uuid,
                hidden: true
            },
            {
                key: subscriptionFields.STATUS.key,
                value: 'RESTOCKABLE',
                hidden: true
            }
        ]
    };

    // State
    const [step, setStep] = useState(0);
    const [selectedSubscriptions, setSelectedSubscriptions] = useState(getInitialValueForRowSelected());
    const [isLoadingAction, setIsLoadingAction] = useState(false);
    const [errorsArray, setErrorsArray] = useState([]);

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchSubscriptions(true);
        toast({
            description: <Entity entity='modal.multiple_rewarehouse.success' namespace={nsTerminal} />,
            variant: 'success'
        });
    };
    const rewarehousePosTerminal = actionsPosTerminals.rewarehousePosTerminal(() => { }, () => { });

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.select_terminals`,
                disabledNext: !Object.keys(selectedSubscriptions.rowIds).length
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirmation`,
                disabledNext: false
            },
            2: {
                stepTitle: '',
                disabledNext: false
            }
        }),
        [selectedSubscriptions]
    );

    // Functions
    //multi-rewarehouse actions need to be chained
    const processRewarehouseAction = async (subscriptionsList) => {
        // Reset errors list
        setErrorsArray([]);

        // Set loading to true
        setIsLoadingAction(true);

        // Launch Api calls
        const results = await Promise.allSettled(
            subscriptionsList.map(async (subscription) => {
                try {
                    await rewarehousePosTerminal.mutateAsync({ subscriptionId: subscription.uuid });
                    return;
                } catch (error) {
                    const err = new Error(`${subscription.uuid}: ${error.message || error}`);
                    err.subscription = subscription;
                    throw err;
                }
            })
        );

        setIsLoadingAction(false);

        // Set found errors
        const errors = results
            .filter(err => err.status === 'rejected')
            .map(err => {
                const { subscription, error } = err.reason;
                const errorCode = error?.response?.data?.errorCode ?? error?.code ?? 'UNKNOWN_ERROR';
                return { subscription, errorCode };
            });

        // On Error
        if (errors.length > 0) {
            setErrorsArray(errors);
            setStep(2);
        }
        // On Success
        else {
            onSuccess();
        }
    };

    const handleValidateButton = async () => {
        await processRewarehouseAction(selectedSubscriptions.rowsData);
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step === 0 ? 'max-w-6xl' : 'max-w-3xl'}>
                <HeaderTitle title='modal.multiple_rewarehouse.title' namespace={nsTerminal} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: MAX_STEPS_NUMBER }}
                        namespace={nsTerminal}
                        className={'mb-2 flex'}
                    />
                    {step === 0 &&
                        <SubscriptionsTable
                            filtersEventName={setFiltersEventName('subscriptions', true)}
                            customQuery={customQuery}
                            rowsSelected={selectedSubscriptions}
                            setRowsSelected={setSelectedSubscriptions}
                        />
                    }
                    {step === 1 &&
                        <div className='space-y-4'>
                            <div className='text-center'>
                                <Entity entity='modal.multiple_rewarehouse.confirmation.text_1' namespace={nsTerminal} />
                            </div>
                            <div className='text-center'>
                                <Entity entity='modal.multiple_rewarehouse.confirmation.text_2' namespace={nsTerminal} />
                            </div>
                            <div className='text-center'>
                                <Entity entity='modal.multiple_rewarehouse.confirmation.question' namespace={nsTerminal} />
                            </div>
                        </div>
                    }
                    {step === 2 &&
                        <div className='space-y-2 text-center'>
                            {errorsArray.length === 1 ?
                                <Entity entity='modal.multiple_rewarehouse.error_pos_terminal' namespace={nsTerminal} />
                                :
                                <Entity entity='modal.multiple_rewarehouse.error_pos_terminals' namespace={nsTerminal} />
                            }
                            <div className='error-list-recap'>
                                <ul>
                                    {
                                        errorsArray.map((item) =>
                                            <li key={item.subscription.uuid}>
                                                {getObjectAttributeFromStringKey(item.subscription,
                                                    generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.SERIAL_NUMBER)
                                                )}
                                            </li>
                                        )
                                    }
                                </ul>
                            </div>
                        </div>
                    }
                </ModalBody>
                {(step === 0 || step === 1) &&
                    <FooterStep
                        step={step}
                        setStep={setStep}
                        stepsNumber={MAX_STEPS_NUMBER}
                        isLoading={isLoadingAction}
                        onValidate={async () => {
                            await handleValidateButton();
                        }
                        }
                        disableValidation={disabledNext}
                    />
                }
                {step === 2 &&
                    <FooterClose />
                }
            </ModalContent>
        </Modal>
    );
}

ModalMutipleRewarehouse.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalMutipleRewarehouse;